import withUser from '@/withUser';
import Head from 'next/head';
import Header, { NavCategory } from '@components/Header';
import React, { useMemo, useState } from 'react';
import FrontLayout from '@layouts/FrontLayout';
import UIWidth from '@components/presentational/UIWidth';
import { Publication as PublicationConstants } from '@/types/constants';
import { NextPageContext } from 'next';
import { SearchResult } from '@/types/search/searchresult';
import { Resource } from '@/types/core/Resource';
import { searchPublications } from '@/api/publication';
import SimpleCard from '@components/presentational/Cards/SimpleCard';
import {
  Box,
  Button,
  Flex,
  Heading,
  Icon,
  Link,
  LinkBox,
  LinkOverlay,
  Select,
  Show,
  SimpleGrid,
  Text,
  useDisclosure,
  useMediaQuery,
  useToast,
  VStack,
} from '@chakra-ui/react';
import RessourcesPicto from '@/components/presentational/img/icons/RessourcesPicto';
import HeroBanner from '@components/presentational/Banners/HeroBanner';
import Breadcrumb, { BreadcrumbItem } from '@components/app/Breadcrumb';
import BgMasqueHaut from '@/components/presentational/img/v2024/BgMasqueHaut.svg';
import {
  PiBookmarkSimpleThin,
  PiChartBarThin,
  PiFoldersThin,
  PiNotePencilThin,
} from 'react-icons/pi';
import CardBody from '@components/presentational/Cards/Parts/CardBody';
import CardFooter from '@components/presentational/Cards/Parts/CardFooter';
import BackgroundResources1 from '@/public/resources/section_resources_1.svg';
import IconForButton from '@components/presentational/IconForButton';
import { ResourceCard } from '@components/ressource/ResourceCard';
import TextBanner from '@components/presentational/Banners/TextBanner';
import { useSubHeaderCategoriesFromAlgoliaFacetSearch } from '@components/app/hooks/useResource';
import HeaderResourceForm, {
  getRouteFromCategoryValue,
} from '@components/HeaderResourceForm';
import resourceTranslations from '@/translations/resource.fr.yml';
import { fetchLabelizedMainCategories } from '@/components/app/search/fetchSearch';
import { useRouter } from 'next/router';
import NextLink from 'next/link';
import { CheckAuth } from '@components/app/CheckAuth';
import PublishPedagogicalResourceModal from '@components/community/resource/PublishPedagogicalResourceModal';

import RessourcesHero from '../../../app/components/presentational/img/v2024/ressources/RessourcesHero.webp';
import Carousel from '@/components/refonte2024/app/Carousel';

function PageRessources({
  ressources,
  algoliaCategories,
}: {
  ressources: SearchResult<Resource>;
  algoliaCategories: any;
}) {
  const [headerSearch, setHeaderSearch] = useState('');

  const [isHovering, setIsHovering] = useState(false);

  const categoriesForSubheader = useSubHeaderCategoriesFromAlgoliaFacetSearch(
    algoliaCategories
  );

  const CECRLevels = useMemo(
    () =>
      Object.entries(resourceTranslations.cecr_level).map(entry => {
        return {
          value: entry[0] as string,
          label: entry[1] as string,
        };
      }),
    [resourceTranslations]
  );
  const {
    isOpen: isPublishPedagogicalModalOpen,
    onOpen: onPublishPedagogicalModalOpen,
    onClose: onPublishPedagogicalModalClose,
  } = useDisclosure();
  const toast = useToast();
  const router = useRouter();
  const [isLargerThan1024] = useMediaQuery('(min-width: 1024px)');
  return (
    <FrontLayout
      gtmPage={{
        page_type: 'Ressource',
        page_level1: 'Ressources',
      }}
    >
      <Head>
        <>
          <title>
            Ressources pédagogiques pour l'enseignement du FLE | IFprofs
          </title>
          <meta
            key={'description'}
            name="description"
            content="Explorez une vaste sélection de ressources pédagogiques sélectionnées par IFprofs pour enrichir vos cours de français. Accédez à des activités, des fiches pratiques, et des outils innovants pour enseigner la langue française dans le monde"
          />
        </>
      </Head>

      {isLargerThan1024 && (
        <Header mainNavItemActive={NavCategory.Ressources}>
          <HeaderResourceForm
            categories={categoriesForSubheader}
            search={headerSearch}
            setSearch={setHeaderSearch}
          />
        </Header>
      )}
      {!isLargerThan1024 && <Header />}

      <HeaderResourceForm
        categories={categoriesForSubheader}
        search={headerSearch}
        setSearch={setHeaderSearch}
        isRessourcesHomePageMobileOnly
      />

      <HeroBanner
        title="Des ressources de qualité, pour plus d'efficacité"
        image={RessourcesHero.src}
        accentColor="tuile.100"
        textColor="black"
        pictoTopBase="15rem"
        pictoTopSm="6rem"
        pictoLeftBase="-23.5rem"
        picto={
          <>
            <Show breakpoint="(min-width: 48em)">
              <RessourcesPicto
                freeIconColor="white"
                iconAlt=""
                iconWidth="28rem"
              />
            </Show>
            <Show breakpoint="(max-width: 47em)">
              <RessourcesPicto
                freeIconColor="white"
                iconAlt=""
                iconWidth="60em"
              />
            </Show>
          </>
        }
      >
        <p>
          L'équipe IFprofs a selectionné et classé pour vous des centaines de{' '}
          <strong>
            ressources pédagogiques dédiées à l'enseignement du français
          </strong>
          . Vous y retrouverez notamment de nombreuses{' '}
          <strong>
            ressources spécialisées en FLE (Français Langue Étrangère) et les
            domaines qui y sont liés
          </strong>
          . Notre objectif : vous faire gagner du temps !
        </p>
      </HeroBanner>

      <Breadcrumb
        breadcrumbItems={[
          { label: 'Accueil', as: '/', href: '/' },
          { label: 'Ressources' },
        ]}
      >
        <BreadcrumbItem label="Accueil" as={'/'} href={'/'} />
        <BreadcrumbItem label="Ressources" />
      </Breadcrumb>

      <Box
        as="section"
        mt="2rem"
        backgroundImage={{ md: `url("${BgMasqueHaut.src}")` }}
        backgroundRepeat={{ md: 'no-repeat' }}
        backgroundSize={{ md: '512rem auto' }}
        backgroundColor={{ md: 'tuile.25' }}
      >
        <Box
          backgroundImage={{ md: `url("${BackgroundResources1.src}")` }}
          backgroundSize={{ md: '100% auto' }}
          backgroundRepeat={{ md: 'no-repeat' }}
        >
          <UIWidth>
            <Box
              display={{ md: 'flex' }}
              gap={{ md: '1.25rem' }}
              pb="2rem"
              alignItems={{ md: 'stretch' }}
            >
              <Flex
                direction="column"
                alignItems="stretch"
                width={{ md: '33.3333%' }}
                flexShrink={{ md: 0 }}
                gap={{ base: '1rem', md: '1.25rem' }}
              >
                <Flex>
                  <Box
                    as="span"
                    color="brand.100"
                    width={{ base: '3rem', md: '4.5rem' }}
                    height={{ base: '3rem', md: '4.5rem' }}
                    aria-hidden="true"
                    flexShrink={0}
                  >
                    <Icon
                      as={PiBookmarkSimpleThin}
                      boxSize={{ base: '3rem', md: '4.5rem' }}
                      color="brand.100"
                    />
                  </Box>
                  <Heading
                    as="h2"
                    variant="h2"
                    mb={{ base: '1rem', md: '2rem' }}
                    mt={{ md: '.75rem' }}
                    alignItems="center"
                    fontSize={{ base: '2rem', md: '2.25rem' }}
                    fontWeight="600"
                    color="brand.100"
                    gap=".625rem"
                  >
                    Choisissez un thème
                  </Heading>
                </Flex>
                <Box flexGrow={{ md: 0 }} flexShrink={{ md: 1 }}>
                  <Text fontSize={{ md: '1.25rem' }} mb="1rem">
                    Retrouvez toutes les ressources classées par grands domaines
                    du FLE. Vous pourrez ensuite les filtrer par
                    sous-thématiques, publics, niveaux, et compétences pour
                    affiner votre recherche.
                  </Text>
                  <Text fontSize={{ md: '1.25rem' }} mb="1rem">
                    Notre objectif : vous faire gagner du temps lors de la
                    préparation de vos cours et offrir aux apprenants des
                    activités riches et variées.
                  </Text>
                </Box>
              </Flex>
              <Select
                display={{ sm: 'none' }}
                defaultValue="placeholder"
                onChange={async e => {
                  const route = getRouteFromCategoryValue(e.target.value);
                  if (route) {
                    await router.push(
                      {
                        pathname: '/ressources/recherche',
                        query: { category: e.target.value },
                      },
                      route
                    );
                  }
                }}
              >
                <option disabled value="placeholder">
                  Sélectionner un thème
                </option>
                <optgroup>
                  {categoriesForSubheader.map((category, index) => (
                    <Box
                      as="option"
                      key={index}
                      color={
                        category.value === 'category.if_resources'
                          ? 'brand.100'
                          : null
                      }
                      value={category.value}
                    >
                      {category.label} ({category.count.toLocaleString()})
                    </Box>
                  ))}
                </optgroup>
              </Select>
              <SimpleGrid
                display={{ base: 'none', sm: 'grid' }}
                columns={{ sm: 2 }}
                overflowX={{ sm: 'hidden' }}
                overflowY="hidden"
                pr={{ sm: 0 }}
                gap={{ sm: '1rem', md: '1.25rem' }}
                alignItems="stretch"
                pb={{ sm: '1rem', md: 'revert' }}
                mb={{ sm: '1rem', md: 0 }}
              >
                {categoriesForSubheader.map((category, index) => (
                  <LinkBox
                    onMouseEnter={() => setIsHovering(true)}
                    onMouseLeave={() => setIsHovering(false)}
                    key={index}
                  >
                    <SimpleCard
                      bg={
                        category.value === 'category.if_resources'
                          ? 'tuile.25'
                          : null
                      }
                      width={{ base: 'calc(100vw - 4rem)', sm: 'revert' }}
                      accentColor="tuile.100"
                      key={index}
                      height="100%"
                      mt="0"
                      aspectRatio={{ sm: '2.5 / 1 ', md: '2 / 1' }}
                      hoveredBg={isHovering ? 'tuile.10' : false}
                    >
                      <CardBody>
                        <Flex
                          fontWeight="600"
                          color="brand.100"
                          fontSize={{ base: '1.125rem', md: '1.5rem' }}
                          height="100%"
                          alignItems="center"
                          textAlign="center"
                          justifyContent="center"
                          lineHeight="1.2"
                        >
                          <NextLink
                            href={{
                              pathname: '/ressources/recherche',
                              query: { category: category.value },
                            }}
                            as={getRouteFromCategoryValue(category.value)}
                            passHref
                          >
                            <LinkOverlay>{category.label}</LinkOverlay>
                          </NextLink>
                        </Flex>
                      </CardBody>
                      <CardFooter>
                        <Text
                          as="span"
                          display="block"
                          textAlign="center"
                          width="100%"
                        >
                          {category.count.toLocaleString()} ressource
                          {category.count > 1 ? 's' : null}
                        </Text>
                      </CardFooter>
                    </SimpleCard>
                  </LinkBox>
                ))}
              </SimpleGrid>
            </Box>
          </UIWidth>
        </Box>
      </Box>

      <Box
        width="100%"
        background={'tuile.100'}
        py={{ base: '2rem', sm: '5rem' }}
        position="relative"
      >
        <UIWidth>
          <VStack spacing={{ base: '1.25rem', md: '4rem' }}>
            <Flex gap="1.25rem">
              <Icon
                as={PiChartBarThin}
                boxSize={{ sm: '2rem', md: '4.5rem' }}
                mt={{ sm: '.25rem', md: 0 }}
                display={{ base: 'none', sm: 'inline-block' }}
              />
              <VStack spacing={{ base: '1.25rem', md: '2rem' }} align="stretch">
                <Heading
                  as="h2"
                  variant="h2"
                  mt={{ md: '.5rem' }}
                  display={{ base: 'flex', sm: 'block' }}
                  gap="1rem"
                  color="inherit"
                >
                  <Icon
                    as={PiChartBarThin}
                    boxSize="2rem"
                    mt=".25rem"
                    display={{ sm: 'none' }}
                  />
                  Explorer les ressources par niveaux
                </Heading>
                <Text fontSize={{ base: '1rem', md: '1.25rem' }}>
                  Vous travaillez avec un niveau de langue en particulier ?
                  Trouvez les{' '}
                  <Text as="strong" fontWeight="600">
                    ressources et exercices de francais
                  </Text>{' '}
                  adaptés à votre public cible. Du français a1 à c2, vous y
                  retrouverez notamment des fiches pédagogiques FLE, des jeux à
                  imprimer, des exercices de grammaire, de conjugaison et bien
                  d'autres activités ludiques dédiées à l'
                  <Text as="strong" fontWeight="600">
                    enseignement du français à l'étranger
                  </Text>
                  .
                </Text>
              </VStack>
            </Flex>
            <Flex
              alignSelf="stretch"
              gap="1.25rem"
              justifyContent="space-between"
              flexWrap="wrap"
              mx={{ sm: '3rem', md: 'revert' }}
            >
              {CECRLevels.map((level, index) => (
                <NextLink
                  key={index}
                  href={{
                    pathname: '/ressources/recherche',
                    query: { CECRLevel: level.value },
                  }}
                  as={`/ressources/niveaux/${level.value}`}
                  passHref
                >
                  <Link
                    display="flex"
                    width={{ base: '4rem', md: '8rem' }}
                    aspectRatio={1}
                    backgroundColor="tuile.25"
                    border="solid 1px"
                    borderColor="brand.100"
                    alignItems="center"
                    justifyContent="center"
                    fontSize={{ md: '1.75rem' }}
                    color="brand.100"
                    borderRadius="full"
                    mx={{ base: 'auto', sm: 'revert' }}
                    _hover={{
                      textDecoration: 'none',
                      backgroundColor: 'tuile.50',
                      color: 'black',
                    }}
                  >
                    {level.label}
                  </Link>
                </NextLink>
              ))}
            </Flex>
          </VStack>
        </UIWidth>
      </Box>

      <Box width="100%" py={{ base: '2rem', sm: '5rem' }} position="relative">
        <UIWidth>
          <Heading as="h2" variant="h2" mb="0">
            Ressources à la Une
          </Heading>
          <Carousel variant={'tuile'}>
            {ressources.items &&
              ressources.items.map(resource => (
                <ResourceCard
                  key={resource.id}
                  extraQueryParamsForDetailLink={{
                    isFromLabelizedRessourcesPage: 'true',
                  }}
                  publication={resource}
                />
              ))}
          </Carousel>
          <Text textAlign="center" pt={{ base: '1rem', md: '4rem' }}>
            <NextLink href="/ressources/recherche" passHref>
              <Button as="a" width={{ base: '100%', sm: 'revert' }}>
                <IconForButton as={PiFoldersThin} mr=".75em" />
                <Text as="span" lineHeight={{ base: '1', sm: null }}>
                  Voir toutes les ressources
                </Text>
              </Button>
            </NextLink>
          </Text>
        </UIWidth>
      </Box>
      <TextBanner
        title="Partagez vos ressources"
        text={`<p style="margin: revert">Vos contributions sont précieuses et vous pouvez en faire profiter toute la communauté IFprofs.</p>
             <p style="margin: revert">Les ressources que vous partagez sont publiées automatiquement dans votre espace pays. Nous vous invitons également à nous indiquer celles qui peuvent avoir leur place dans cette rubrique accessible à tous.</p>`}
        accentColor="tuile.100"
        button={
          <CheckAuth
            actionId="publish"
            actionAfterLogin={onPublishPedagogicalModalOpen}
          >
            {onClick => (
              <Button
                onClick={onClick}
                variant="tileOutline"
                size={['sm', '', 'md']}
              >
                <Icon as={PiNotePencilThin} boxSize={6} mr={2} />
                Partager une ressource
              </Button>
            )}
          </CheckAuth>
        }
        picto={
          <RessourcesPicto
            freeIconColor="white"
            iconAlt=""
            iconWidth="12.5em"
          />
        }
      />
      {isPublishPedagogicalModalOpen && (
        <PublishPedagogicalResourceModal
          isOpen
          onClose={onPublishPedagogicalModalClose}
          onSubmitEnd={async (values, published) => {
            toast({
              title: published ? 'Ressource publiée' : 'Ressource enregistrée',
              status: 'success',
              duration: 9000,
              isClosable: true,
            });
            onPublishPedagogicalModalClose();
          }}
        />
      )}
    </FrontLayout>
  );
}

PageRessources.getInitialProps = async (context: NextPageContext) => {
  const [ressources, algoliaCategories] = await Promise.all([
    searchPublications(
      {
        fields: {
          publication:
            'title,createdAt,updatedAt,slug,text,countSelections,countRecommendations',
          resource: 'subtitle,requiredCECRLevels,categories,criteria',
          user: 'displayName',
          file: 'filename',
        },
        relationIncludes: ['author', 'author.avatar', 'resource', 'image'],
        filters: {
          labelized: 'true',
          featured: 'true',
          type: 'resource',
          publishedStatus: PublicationConstants.STATE_PUBLISHED,
        },
        orders: ['random'],
        pagination: {
          page: 1,
          itemsPerPage: 4,
          countTotalResults: true,
        },
      },
      { context }
    ),
    fetchLabelizedMainCategories(),
  ]);

  return {
    ressources,
    algoliaCategories,
  };
};
export default withUser(PageRessources);
